/**
 * useSPLHData Hook
 *
 * Custom React hook for fetching and managing SPLH Dashboard data.
 * Handles data fetching, loading states, error handling, and debouncing.
 *
 * Architecture:
 * - Fetches data via splhDataService when clientId or dateRange changes
 * - Debounces dateRange changes (300ms) to prevent excessive requests
 * - Returns ready-to-render data from backend (no frontend calculations)
 * - Provides refetch function for manual refresh
 *
 * @module pages/common/dashboards/SPLHDashboard/hooks/useSPLHData
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchSPLHDashboardData, clearCache } from '../services/splhDataService';
import * as logger from '../../../../../utils/logger';

/**
 * Debounce delay in milliseconds
 * @constant {number}
 */
const DEBOUNCE_DELAY = 300;

/**
 * Hook state structure
 * @typedef {Object} UseSPLHDataState
 * @property {Object|null} kpis - KPI summary data (currentSPLH, weekAvg, monthAvg, targetDelta, trend)
 * @property {Array} trendData - Trend data points for chart
 * @property {Array} hierarchyData - Hierarchy nodes for drilldown table
 * @property {Object|null} breakdownData - Breakdown data (topPerformers, bottomPerformers, byDayOfWeek, byDaypart)
 * @property {Array} alerts - Alert notifications
 * @property {Object|null} hierarchyMetadata - Hierarchy configuration with level display names
 * @property {boolean} loading - Whether data is being fetched
 * @property {string|null} error - Error message if fetch failed
 */

/**
 * Hook return value
 * @typedef {Object} UseSPLHDataReturn
 * @property {Object|null} kpis - KPI summary data
 * @property {Array} trendData - Trend data points
 * @property {Array} hierarchyData - Hierarchy nodes
 * @property {Object|null} breakdownData - Breakdown data
 * @property {Array} alerts - Alert notifications
 * @property {Object|null} hierarchyMetadata - Hierarchy configuration
 * @property {boolean} loading - Loading state
 * @property {string|null} error - Error message
 * @property {Function} refetch - Function to manually refetch data
 */

/**
 * Custom hook for fetching SPLH Dashboard data
 *
 * @param {string} clientId - Client identifier (e.g., 'dem50')
 * @param {Object} dateRange - Date range for the query
 * @param {string} dateRange.startDate - Start date in YYYY-MM-DD format
 * @param {string} dateRange.endDate - End date in YYYY-MM-DD format
 * @returns {UseSPLHDataReturn} Dashboard data and state
 *
 * @example
 * const {
 *   kpis,
 *   trendData,
 *   hierarchyData,
 *   breakdownData,
 *   alerts,
 *   hierarchyMetadata,
 *   loading,
 *   error,
 *   refetch
 * } = useSPLHData('dem50', { startDate: '2025-11-18', endDate: '2025-11-24' });
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage message={error} onRetry={refetch} />;
 *
 * return <Dashboard kpis={kpis} trendData={trendData} ... />;
 */
export function useSPLHData(clientId, dateRange) {
  // State for dashboard data
  const [kpis, setKpis] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [hierarchyData, setHierarchyData] = useState([]);
  const [breakdownData, setBreakdownData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [hierarchyMetadata, setHierarchyMetadata] = useState(null);

  // State for loading and error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Ref for tracking mounted state (cleanup)
  const isMountedRef = useRef(true);

  // Ref for debounce timeout
  const debounceTimeoutRef = useRef(null);

  // Ref for tracking the latest request (to handle race conditions)
  const requestIdRef = useRef(0);

  /**
   * Fetches dashboard data from the backend
   *
   * @param {boolean} [skipCache=false] - Whether to skip the cache
   * @private
   */
  const fetchData = useCallback(
    async (skipCache = false) => {
      // Validate inputs before fetching
      if (!clientId || !dateRange?.startDate || !dateRange?.endDate) {
        logger.debug('useSPLHData: Skipping fetch - missing required inputs', {
          clientId,
          dateRange,
        });
        return;
      }

      // Increment request ID to track this specific request
      const currentRequestId = ++requestIdRef.current;

      setLoading(true);
      setError(null);

      logger.debug('useSPLHData: Fetching data', {
        clientId,
        dateRange,
        skipCache,
        requestId: currentRequestId,
      });

      try {
        const data = await fetchSPLHDashboardData(clientId, dateRange, { skipCache });

        // Check if component is still mounted and this is still the latest request
        if (!isMountedRef.current) {
          logger.debug('useSPLHData: Component unmounted, discarding response');
          return;
        }

        if (currentRequestId !== requestIdRef.current) {
          logger.debug('useSPLHData: Stale response, discarding', {
            currentRequestId,
            latestRequestId: requestIdRef.current,
          });
          return;
        }

        // Update all state with the response data
        setKpis(data.kpis || null);
        setTrendData(data.trendData || []);
        setHierarchyData(data.hierarchyData || []);
        setBreakdownData(data.breakdownData || null);
        setAlerts(data.alerts || []);
        setHierarchyMetadata(data.hierarchyMetadata || null);
        setError(null);

        // Complete debug logging of the query response
        console.log('[useSPLHData] Complete query response:', JSON.stringify(data, null, 2));

        logger.debug('useSPLHData: Data fetched successfully', {
          clientId,
          dateRange,
          requestId: currentRequestId,
        });
      } catch (err) {
        // Check if component is still mounted and this is still the latest request
        if (!isMountedRef.current || currentRequestId !== requestIdRef.current) {
          return;
        }

        logger.error('useSPLHData: Fetch failed', {
          clientId,
          dateRange,
          error: err.message,
          requestId: currentRequestId,
        });

        setError(err.message || 'Failed to load dashboard data');
      } finally {
        // Only update loading state if still mounted and this is the latest request
        if (isMountedRef.current && currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [clientId, dateRange]
  );

  /**
   * Debounced fetch function
   * Waits for DEBOUNCE_DELAY ms after the last call before fetching
   *
   * @private
   */
  const debouncedFetch = useCallback(() => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      fetchData(false);
    }, DEBOUNCE_DELAY);
  }, [fetchData]);

  /**
   * Manual refetch function
   * Clears cache and fetches fresh data immediately (no debounce)
   */
  const refetch = useCallback(() => {
    // Clear any pending debounced fetch
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Clear cache for this client and fetch fresh data
    if (clientId) {
      clearCache(clientId);
    }

    fetchData(true);
  }, [clientId, fetchData]);

  // Effect to fetch data when clientId or dateRange changes
  useEffect(() => {
    // Use debounced fetch for dateRange changes
    debouncedFetch();

    // Cleanup function
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [debouncedFetch]);

  // Effect to track mounted state
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    // Data
    kpis,
    trendData,
    hierarchyData,
    breakdownData,
    alerts,
    hierarchyMetadata,

    // State
    loading,
    error,

    // Actions
    refetch,
  };
}

export default useSPLHData;
